import { useLifecyclePersistence, useSyncLifecycleToExistingAtom } from '@/ui'
import { ConformedCampaignStoryAd, MediaAsset } from '@/ui'
import { useImageCacheWriter } from '@/ui'
import { trackDesignSaved } from '@/ui'
import { cacheEditorImages, type EditorImage } from '@/ui/editor/image-readiness'
import { useSetAtom } from 'jotai'
import { useEffect } from 'react'
import {
	storyAdAtom,
	storyAdHasGeneratedContentAtom,
	storyAdImageHistoryAtom,
	storyAdSavingAtom,
	storyAdStatusAtom,
	useStoryAdListener,
} from '../state'

export type UseStoryEditorArguments = {
	storyAd?: ConformedCampaignStoryAd

	// should reflect if the story ad's query is pending
	isPending: boolean

	// should reflect if the story ad's data indicate it's still being generated
	isGenerating: boolean

	// should reflect if a chat tool is currently executing on the story ad
	isToolExecuting?: boolean

	//
	imagesHistory?: Array<MediaAsset>

	// pass in the existing image, if present. This doesn't affect the actual
	// bound image; it's just for caching the image URL.
	image?: EditorImage

	// callback to handle story ad changes; will be debounced internally
	onStoryAdChange: (value: ConformedCampaignStoryAd) => void

	// optional version tracking for preventing race conditions
	storyAdVersion?: number | string | Date | null
}

export function useStoryEditor(
	args: UseStoryEditorArguments,
): { lifecycle: 'idle' | 'pending' | 'generating' | 'tool-executing' | 'ready' } {
	const {
		storyAd,
		isPending,
		isGenerating,
		isToolExecuting = false,
		imagesHistory,
		image,
		onStoryAdChange,
		storyAdVersion,
	} = args

	const { cacheImageUrl } = useImageCacheWriter()

	// Set up lifecycle persistence
	const storyLifecycle = useLifecyclePersistence<ConformedCampaignStoryAd>({
		data: storyAd ?? undefined,
		isIdle: (d) => d == undefined,
		isPending: () => isPending,
		isGenerating: () => isGenerating,
		isToolExecuting,
		save: async (storyData) => {
			await onStoryAdChange(storyData)
			if (storyData?.id) {
				trackDesignSaved('story', storyData.id)
			}
		},
		debounceMs: 800,
		getVersion: () => storyAdVersion ?? null,
		debug: false,
		name: 'useStoryEditor',
	})

	// Sync to existing atoms
	// Note: storyAdAtom can be undefined when data hasn't loaded yet
	useSyncLifecycleToExistingAtom<ConformedCampaignStoryAd, ConformedCampaignStoryAd | undefined>(
		storyLifecycle,
		{
			dataAtom: storyAdAtom,
			useDataListener: useStoryAdListener,
			lifecycleAtom: storyAdStatusAtom,
			persistenceAtom: storyAdSavingAtom,
		},
	)

	// Image caching (unchanged from original implementation)
	const setStoryAdImageHistory = useSetAtom(storyAdImageHistoryAtom)
	const setHasGeneratedContent = useSetAtom(storyAdHasGeneratedContentAtom)
	useEffect(() => {
		cacheEditorImages(image, imagesHistory, cacheImageUrl)
		if (imagesHistory) {
			setStoryAdImageHistory(imagesHistory)
		}
	}, [imagesHistory, setStoryAdImageHistory, cacheImageUrl, image])

	useEffect(() => {
		if (!storyAd) {
			setHasGeneratedContent(false)
			return
		}

		if (!isGenerating) {
			setHasGeneratedContent(true)
		}
	}, [storyAd, isGenerating, setHasGeneratedContent])

	return { lifecycle: storyLifecycle.lifecycle }
}
